import React, { useState } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { updateSupplier } from '../../redux/features/supplier.features';
import { toast } from 'react-toastify';
import { FaUserPlus, FaSave } from 'react-icons/fa';

const QuickAgentModal = ({ show, onHide, supplier, onSuccess }) => {
    const dispatch = useDispatch();
    const [agentData, setAgentData] = useState({
        name: '',
        designation: '',
        phone: ''
    });
    const [loading, setLoading] = useState(false);

    const handleSave = async () => {
        if (!agentData.name || !supplier) {
            toast.warning("Agent Name is required");
            return;
        }

        // Duplicate Check (Local)
        if (supplier.contacts?.some(c => c.name.toLowerCase() === agentData.name.trim().toLowerCase())) {
            toast.warning("Agent already exists for this supplier");
            return;
        }

        setLoading(true);
        try {
            // Prepare updated supplier object
            // We append to the existing contacts array
            const updatedContacts = [
                ...(supplier.contacts || []),
                {
                    ...agentData,
                    isPrimary: false // Default to false for quick add
                    // The backend or mongoose schema might generate _id for subdocuments automatically if defined in schema
                    // If not, we might rely on the backend response to get the ID.
                }
            ];

            const payload = {
                ...supplier,
                contacts: updatedContacts
            };

            const result = await dispatch(updateSupplier(payload)).unwrap();

            if (result) {
                // Find the newly added contact to pass back
                // Assuming it's the last one or matching by name (risky if duplicates allowed server side, but we checked locally)
                // Better approach: filter by the name we just added
                const newAgent = result.contacts.find(c => c.name === agentData.name.trim());

                toast.success(`Agent ${agentData.name} added successfully`);
                if (onSuccess && newAgent) onSuccess(newAgent, result);

                setAgentData({ name: '', designation: '', phone: '' });
                onHide();
            }
        } catch (error) {
            toast.error(error.message || "Failed to add agent");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal show={show} onHide={onHide} centered backdrop="static" size="sm">
            <Modal.Header closeButton className="bg-light border-0">
                <Modal.Title className="fw-bold fs-6 d-flex align-items-center gap-2">
                    <FaUserPlus className="text-success" /> Add Sales Rep
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-4">
                <div className="mb-3">
                    <label className="x-small fw-bold text-muted text-uppercase mb-2 d-block">Supplier Reference</label>
                    <div className="p-2 bg-light border rounded fw-bold text-dark small">
                        {supplier ? supplier.business_name : "No Supplier Selected"}
                    </div>
                </div>


                <Form.Group className="mb-3">
                    <label className="x-small fw-bold text-muted text-uppercase mb-1">Agent Name *</label>
                    <Form.Control
                        type="text"
                        placeholder="e.g. John Doe"
                        className="fw-bold shadow-none"
                        value={agentData.name}
                        onChange={(e) => setAgentData({ ...agentData, name: e.target.value })}
                        autoFocus
                    />
                </Form.Group>

                <Form.Group className="mb-3">
                    <label className="x-small fw-bold text-muted text-uppercase mb-1">Designation</label>
                    <Form.Control
                        type="text"
                        placeholder="e.g. Sales Executive"
                        className="shadow-none"
                        value={agentData.designation}
                        onChange={(e) => setAgentData({ ...agentData, designation: e.target.value })}
                    />
                </Form.Group>

                <Form.Group className="mb-0">
                    <label className="x-small fw-bold text-muted text-uppercase mb-1">Contact Phone</label>
                    <Form.Control
                        type="text"
                        placeholder="Phone Number"
                        className="shadow-none"
                        value={agentData.phone}
                        onChange={(e) => setAgentData({ ...agentData, phone: e.target.value })}
                    />
                </Form.Group>
            </Modal.Body>
            <Modal.Footer className="border-0 p-4 pt-0">
                <Button variant="light" className="fw-bold px-4 rounded-pill" onClick={onHide}>
                    Cancel
                </Button>
                <Button
                    variant="success"
                    className="fw-bold px-4 rounded-pill shadow-sm"
                    onClick={handleSave}
                    disabled={loading || !supplier}
                >
                    {loading ? "Saving..." : <><FaSave className="me-2" /> Save Agent</>}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default QuickAgentModal;
